import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';

/**
 * Sender domain authentication health (M1, #752).
 *
 * Fetches ONCE for the whole workspace, not once per connected sender. The
 * checks are cached per DOMAIN server-side and several senders normally share
 * one -- 8 accounts resolve to 2 domains here -- so a per-row fetch would ask
 * for the same answer N times.
 *
 * Distinct from `components/enterprise/SenderHealthPanel`, which is an
 * unrelated RPC-backed enterprise monitoring feature.
 */

export type CheckStatus = 'pass' | 'warn' | 'fail' | 'unknown';

export interface DomainFinding {
  id: string;
  status: CheckStatus;
  title: string;
  /** What we observed. PLAIN TEXT -- contains third-party DNS content, so it
   *  must never be rendered as HTML. */
  detail: string;
  /** Why a non-technical sender should care. Shown in the "?" popover. */
  whyItMatters: string;
  /** Slug into /help/email-campaigns/<slug>. */
  helpArticleSlug: string;
}

export interface DomainHealth {
  domain: string;
  checkedAt: string;
  overall: CheckStatus;
  findings: DomainFinding[];
}

interface State {
  /** Which workspace `byDomain` belongs to. Without this the cache accumulates
   *  entries across every workspace visited, because SenderSettings is not
   *  remounted on a workspace switch (#819 R1). */
  workspaceId: string | undefined;
  byDomain: Record<string, DomainHealth>;
  loading: boolean;
  /** Domain currently being re-checked, so only that row spins. */
  refreshing: string | null;
  error: string | null;
}

export function useSenderDomainHealth(workspaceId: string | undefined) {
  // Monotonic request id. A background load and a forced re-check are
  // independent in-flight calls; without this a slow background response can
  // land after a fresher re-check and overwrite it with stale data, and two
  // overlapping re-checks stop each other's spinner (#819 R2).
  const reqIdRef = useRef(0);

  const [state, setState] = useState<State>({
    workspaceId: undefined,
    byDomain: {},
    loading: false,
    refreshing: null,
    error: null,
  });

  const load = useCallback(
    async (opts?: { domain?: string; force?: boolean }) => {
      if (!workspaceId) return;
      const reqId = ++reqIdRef.current;
      setState((s) => ({
        ...s,
        // Drop another workspace's results rather than merging into them.
        byDomain: s.workspaceId === workspaceId ? s.byDomain : {},
        workspaceId,
        loading: opts?.force ? s.loading : true,
        refreshing: opts?.force ? (opts.domain ?? null) : s.refreshing,
        error: null,
      }));
      try {
        const { data, error } = await supabase.functions.invoke<{ results: DomainHealth[] }>(
          'sender-domain-health',
          {
            body: {
              workspace_id: workspaceId,
              domains: opts?.domain ? [opts.domain] : undefined,
              force: opts?.force,
            },
          },
        );
        if (error) throw error;
        // A superseded response must not clobber fresher state.
        if (reqId !== reqIdRef.current) return;
        setState((s) => ({
          ...s,
          byDomain: (data?.results ?? []).reduce(
            (acc, r) => ({ ...acc, [r.domain]: r }),
            { ...s.byDomain },
          ),
          loading: false,
          refreshing: null,
        }));
      } catch (err) {
        // Feature-level fetch, so report it. A silent failure here looks
        // identical to "this domain has no problems" -- the exact confusion
        // this feature exists to remove.
        console.error('[SenderDomainHealth] fetch failed:', err);
        captureError(err as Error, { feature: 'sender-domain-health' });
        if (reqId !== reqIdRef.current) return;
        setState((s) => ({
          ...s,
          loading: false,
          refreshing: null,
          error: 'We could not load the email setup checks.',
        }));
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    void load();
    // Bumping the id on cleanup invalidates any response still in flight, which
    // also covers unmount and the StrictMode double-invoke (#819 R3).
    return () => { reqIdRef.current += 1; };
  }, [load]);

  return {
    byDomain: state.byDomain,
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
    recheck: (domain: string) => load({ domain, force: true }),
  };
}
